import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  PageHeader, Panel, Button, IconButton, Field, TextInput, TextArea, Select, Checkbox, RadioList, Tag, Banner,
  EmptyState, Spinner, ProgressBar, Dialog, ConfirmDialog, useCkToast,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import { AudioRecorder } from '@NP-Webber/crm-kit';
import api from '../../services/api.js';
import IvrIntroNext from '../ivr/IvrIntroNext.jsx';
import { Tabs, Row, apiError, Toggle } from '../../next/ui';
import { useScreenSettings } from '../../next/related/relatedSettingsStore';

/**
 * תבניות IVR — new design. Same endpoints as IvrTemplates:
 *   /ivr-templates (library, upload / record audio), /ivr-automations/wife-exam-encourage (feedback after an exam,
 *   test call, pending + resend with live progress), /ivr-automations/registration-approved (TTS or recording),
 *   and the intro tab (IvrIntroNext).
 */
const KEY_LABELS = { wife_exam_encourage: 'פידבק אחרי בחינה', registration_approved: 'אישור רישום', general: 'כללי', broadcast: 'שיגור הודעה' };
const DEFAULT_WIFE_NAME = 'פידבק אחרי בחינה';
const DEFAULT_REG_NAME = 'אישור רישום';
const DEFAULT_REG_TTS = 'הרישום למערכת {systemName} הושלם בהצלחה';
const RESEND_REASON_LABELS = {
  no_log: 'לא נשלח כלל', failed: 'נכשל', skipped: 'דולג', not_heard: 'לא ענתה', unknown: 'נשלח, לא ידוע אם נשמע', stuck: 'תקוע',
};
const OLD_NAME = /עידוד\s*לאשה/i;
const displayTemplateName = (name) => {
  const n = String(name || '').trim();
  if (!n || OLD_NAME.test(n)) return DEFAULT_WIFE_NAME;
  return n;
};
const daysAgoIso = (days) => new Date(Date.now() - days * 864e5).toISOString().slice(0, 10);

function StatusToggle({ checked, disabled, onChange }) {
  return (
    <span className={`nx-toggle ${checked ? 'is-on' : 'is-off'}`}>
      {checked ? 'פעיל' : 'כבוי'}
      <Toggle checked={checked} disabled={disabled} onChange={onChange} label={checked ? 'כיבוי האוטומציה' : 'הפעלת האוטומציה'} />
    </span>
  );
}

function Counters({ automation, saving }) {
  return (
    <>
      {saving ? <Spinner size={18} /> : null}
      {automation?.successCount > 0 ? <Tag tone="success">{`נשלחו: ${automation.successCount}`}</Tag> : null}
      {automation?.failureCount > 0 ? <Tag tone="warning">{`כשלים: ${automation.failureCount}`}</Tag> : null}
    </>
  );
}

export default function IvrTemplatesNext() {
  const toast = useCkToast();
  const location = useLocation();
  const navigate = useNavigate();
  const tabFromUrl = () => (new URLSearchParams(location.search).get('tab') === 'intro' ? 'intro' : 'templates');
  const [tab, setTab] = useState(tabFromUrl);
  const [templates, setTemplates] = useState([]);
  const [automation, setAutomation] = useState(null);
  const [regAutomation, setRegAutomation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [savingAuto, setSavingAuto] = useState(false);
  const [savingReg, setSavingReg] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState(DEFAULT_WIFE_NAME);
  const [newKey, setNewKey] = useState('wife_exam_encourage');
  const [creating, setCreating] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [recordTpl, setRecordTpl] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const [testKind, setTestKind] = useState('wife');
  const [testPhone, setTestPhone] = useState('');
  const [testing, setTesting] = useState(false);
  const [resendSince, setResendSince] = useState(() => daysAgoIso(7));
  const [resendUnknown, setResendUnknown] = useState(false);
  const [pending, setPending] = useState(null);
  const [checkingPending, setCheckingPending] = useState(false);
  const [resendConfirmOpen, setResendConfirmOpen] = useState(false);
  const [resendStatus, setResendStatus] = useState(null);
  const audioElRef = useRef(null);
  const fileInputRefs = useRef({});

  useEffect(() => { setTab(tabFromUrl()); }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps
  const changeTab = (value) => {
    setTab(value);
    navigate(value === 'intro' ? '/ivr-templates?tab=intro' : '/ivr-templates', { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [tRes, aRes, rRes] = await Promise.all([
        api.get('/ivr-templates'),
        api.get('/ivr-automations/wife-exam-encourage'),
        api.get('/ivr-automations/registration-approved'),
      ]);
      const raw = tRes.data?.data || [];
      setTemplates(raw.map((t) => (OLD_NAME.test(String(t.name || '')) ? { ...t, name: DEFAULT_WIFE_NAME } : t)));
      setAutomation(aRes.data || null);
      setRegAutomation(rRes.data || null);
      raw.filter((t) => OLD_NAME.test(String(t.name || ''))).forEach((t) => {
        api.patch(`/ivr-templates/${t.id}`, { name: DEFAULT_WIFE_NAME }).catch(() => {});
      });
    } catch (e) {
      setLoadError(apiError(e, 'שגיאה בטעינה'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { audioElRef.current?.pause(); audioElRef.current = null; }, []);

  const saveAutomation = async (patch) => {
    setSavingAuto(true);
    try {
      const { data } = await api.put('/ivr-automations/wife-exam-encourage', { enabled: automation?.enabled, templateId: automation?.templateId, ...patch });
      setAutomation(data);
      toast.success(data.enabled ? 'האוטומציה נשמרה. מבחנים פעילים עודכנו לחזרה ל־/77/exam_done אחרי סיום.' : 'האוטומציה עודכנה');
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בשמירה'));
      await load();
    } finally {
      setSavingAuto(false);
    }
  };

  const saveRegAutomation = async (patch) => {
    setSavingReg(true);
    try {
      const { data } = await api.put('/ivr-automations/registration-approved', {
        enabled: regAutomation?.enabled,
        mode: regAutomation?.mode || 'tts',
        ttsText: regAutomation?.ttsText || DEFAULT_REG_TTS,
        templateId: regAutomation?.templateId,
        ...patch,
      });
      setRegAutomation(data);
      toast.success('אוטומציית אישור רישום עודכנה');
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בשמירה'));
      await load();
    } finally {
      setSavingReg(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const description = newKey === 'registration_approved'
        ? 'הקלטה להודעת אישור רישום — שיחה יוצאת אחרי אישור חבר חדש'
        : 'הקלטה לפידבק אחרי בחינה — שיחה יוצאת אחרי סיום מבחן';
      await api.post('/ivr-templates', { name: newName.trim(), key: newKey, description });
      setCreateOpen(false);
      setNewName(DEFAULT_WIFE_NAME);
      setNewKey('wife_exam_encourage');
      await load();
      toast.success('התבנית נוצרה — כעת העלו הקלטה');
    } catch (e) {
      toast.error(apiError(e, 'שגיאה ביצירת תבנית'));
      await load();
    } finally {
      setCreating(false);
    }
  };

  const uploadBlob = async (templateId, blob, filename) => {
    setUploadingId(templateId);
    try {
      const fd = new FormData();
      fd.append('audio', blob, filename || 'recording.webm');
      await api.post(`/ivr-templates/${templateId}/audio`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setRecordTpl(null);
      await load();
      toast.success('ההקלטה הועלתה וסונכרנה לימות');
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בהעלאה'));
    } finally {
      setUploadingId(null);
    }
  };

  const playAudio = async (tpl) => {
    if (playingId === tpl.id) {
      audioElRef.current?.pause();
      setPlayingId(null);
      return;
    }
    try {
      audioElRef.current?.pause();
      const { data } = await api.get(`/ivr-templates/${tpl.id}/audio`, { responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const el = new Audio(url);
      audioElRef.current = el;
      el.onended = () => { setPlayingId(null); URL.revokeObjectURL(url); };
      await el.play();
      setPlayingId(tpl.id);
    } catch (e) {
      toast.error(apiError(e, 'לא ניתן להשמיע'));
    }
  };

  const openTest = (kind) => { setTestKind(kind); setTestOpen(true); };
  const runTestCall = async () => {
    if (!testPhone.trim()) return;
    setTesting(true);
    try {
      const path = testKind === 'reg' ? '/ivr-automations/registration-approved/test' : '/ivr-automations/wife-exam-encourage/test';
      const { data } = await api.post(path, { phone: testPhone.trim() });
      toast.success(`שיחת בדיקה נשלחה (${data.result?.phone || testPhone})`);
      setTestOpen(false);
    } catch (e) {
      toast.error(apiError(e, 'שיחת הבדיקה נכשלה'));
    } finally {
      setTesting(false);
    }
  };

  const checkPending = async () => {
    setCheckingPending(true);
    try {
      const { data } = await api.get('/ivr-automations/wife-exam-encourage/pending', { params: { since: resendSince, includeUnknown: resendUnknown } });
      setPending(data);
      if (data.status) setResendStatus(data.status);
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בבדיקת ממתינות'));
    } finally {
      setCheckingPending(false);
    }
  };

  const runResend = async () => {
    setResendConfirmOpen(false);
    try {
      const { data } = await api.post('/ivr-automations/wife-exam-encourage/resend', { since: resendSince, includeUnknown: resendUnknown });
      setResendStatus(data.status);
    } catch (e) {
      toast.error(apiError(e, 'שליחה חוזרת נכשלה'));
    }
  };

  // live progress while a resend runs
  useEffect(() => {
    if (!resendStatus?.running) return undefined;
    const timer = setInterval(async () => {
      try {
        const { data } = await api.get('/ivr-automations/wife-exam-encourage/resend');
        setResendStatus(data.status);
        if (!data.status?.running) { setPending(null); load(); }
      } catch { /* next round */ }
    }, 2000);
    return () => clearInterval(timer);
  }, [resendStatus?.running, load]);

  useScreenSettings(tab === 'templates' ? [
    { key: 'ivr-tpl-new', title: 'תבנית חדשה', description: 'הקלטה חדשה לפידבק אחרי בחינה, אישור רישום או כללית', icon: 'plus', onClick: () => setCreateOpen(true) },
    { key: 'ivr-tpl-intro', title: 'פתיח הקו', description: 'הודעת הטלפון והסיכומים שמושמעים לפני התפריט', icon: 'play', onClick: () => changeTab('intro') },
  ] : []);

  const wifeTemplates = templates.filter((t) => t.key === 'wife_exam_encourage');
  const regTemplates = templates.filter((t) => t.key === 'registration_approved');
  const regMode = ['recording', 'tzintuk'].includes(regAutomation?.mode) ? regAutomation.mode : 'tts';
  const templateOptions = (list) => list.map((t) => ({
    value: t.id, label: `${displayTemplateName(t.name)}${t.yemotSynced ? '' : ' (חסרה הקלטה מסונכרנת)'}`, disabled: !t.yemotSynced,
  }));
  const resendDone = resendStatus && !resendStatus.running;

  const onWifeToggle = (on) => {
    if (on) {
      const selected = wifeTemplates.find((t) => t.id === automation?.templateId);
      if (!selected?.yemotSynced) {
        toast.error('קודם צרו תבנית, העלו הקלטה (עד «מסונכרן לימות»), בחרו אותה ברשימה — ואז הפעילו');
        return;
      }
    }
    saveAutomation({ enabled: on });
  };
  const onRegToggle = (on) => {
    if (on) {
      if (regMode === 'tts' && !(regAutomation?.ttsText || '').trim()) { toast.error('קודם הזינו טקסט להקראה — ואז הפעילו'); return; }
      if (regMode === 'recording' && !regTemplates.find((t) => t.id === regAutomation?.templateId)?.yemotSynced) {
        toast.error('קודם צרו תבנית אישור רישום, העלו הקלטה, בחרו אותה — ואז הפעילו');
        return;
      }
    }
    saveRegAutomation({ enabled: on });
  };

  return (
    <div className="nx-stack nx-narrow">
      <PageHeader
        title="תבניות IVR"
        sub={tab === 'intro' ? 'פתיח הקו — הודעת טלפון וסיכומים' : 'הקלטות לשיחות יוצאות והאוטומציות שמשמיעות אותן'}
        primary={tab === 'templates' ? { label: 'תבנית חדשה', onClick: () => setCreateOpen(true) } : undefined}
        more={tab === 'templates' ? [{ key: 'refresh', label: 'רענון', icon: 'refresh', onClick: load }] : []}
      />
      <Tabs
        value={tab}
        onChange={changeTab}
        ariaLabel="תבניות IVR"
        items={[{ value: 'templates', label: 'תבניות ואוטומציות', count: templates.length || null }, { value: 'intro', label: 'פתיח' }]}
      />

      {tab === 'intro' ? <IvrIntroNext /> : loading ? (
        <div className="ck-empty"><Spinner size={28} /></div>
      ) : loadError ? (
        <Banner tone="danger" icon="alert" action={<Button size="sm" onClick={load}>נסו שוב</Button>}>{loadError}</Banner>
      ) : (
        <>
          {/* ── פידבק בחינה ── */}
          <Panel
            title="פידבק בחינה"
            sub="שיחה יוצאת לטלפון האשה אחרי שהבעל מסיים מבחן"
            icon="phone"
            tone="primary"
            actions={<StatusToggle checked={!!automation?.enabled} disabled={savingAuto} onChange={onWifeToggle} />}
          >
            <div className="nx-stack">
              <p className="nx-desc">
                יוצא לטלפון האשה (שדה «טלפון אשה» בכרטיס החבר) אחרי שהבעל מסיים מבחן — בפורטל או בטלפון.
                בשעות השקטות (22:00–10:00) השיחה נדחית ויוצאת ב-10:00 בקמפיין אחד.
              </p>
              <Row align="flex-end">
                <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                  <Field label="תבנית להשמעה">
                    <Select
                      value={automation?.templateId || ''}
                      disabled={savingAuto}
                      placeholder="ללא"
                      onChange={(v) => saveAutomation({ templateId: v || null })}
                      options={templateOptions(wifeTemplates)}
                    />
                  </Field>
                </div>
                <Button icon="phone" disabled={!automation?.templateId || savingAuto} onClick={() => openTest('wife')}>שיחת בדיקה</Button>
                <Counters automation={automation} saving={savingAuto} />
              </Row>

              <hr className="nx-hr" />
              <h3 className="nx-sub-h">שליחה חוזרת</h3>
              <p className="nx-desc">
                שולח שוב לכל מי שהבעל סיים מבחן ולא קיבלה פידבק או לא ענתה. שיחה אחת לכל מספר.
                מי שלא ענתה וחוזרת למערכת תוך 72 שעות שומעת את ההודעה לפני התפריט.
              </p>
              <Row align="flex-end">
                <Field label="מבחנים מתאריך">
                  <TextInput
                    type="date"
                    value={resendSince}
                    disabled={resendStatus?.running}
                    onChange={(e) => { setResendSince(e.target.value); setPending(null); }}
                  />
                </Field>
                <Checkbox
                  checked={resendUnknown}
                  disabled={resendStatus?.running}
                  onChange={(v) => { setResendUnknown(v); setPending(null); }}
                  label="כולל שנשלחו לפני המעקב (לא ידוע אם נשמעו)"
                />
                <Button icon="search" disabled={checkingPending || resendStatus?.running || !automation?.templateId} onClick={checkPending}>
                  {checkingPending ? 'בודק…' : 'בדוק מי ממתינה'}
                </Button>
              </Row>

              {pending && !resendStatus?.running ? (
                <div className="nx-item">
                  <div className="nx-item__main">
                    <b>{`${pending.stats.phones} מספרים ממתינים`}</b>
                    <div className="nx-item__tags">
                      {Object.entries(pending.stats.byReason || {}).map(([k, n]) => <Tag key={k}>{`${RESEND_REASON_LABELS[k] || k}: ${n}`}</Tag>)}
                      {pending.stats.deferred > 0 ? <Tag tone="info">{`ממתינות לסוף השעות השקטות: ${pending.stats.deferred}`}</Tag> : null}
                      {pending.stats.heard > 0 ? <Tag tone="success">{`שמעו: ${pending.stats.heard}`}</Tag> : null}
                      {pending.stats.noPhoneAlt > 0 ? <Tag>{`ללא טלפון אשה: ${pending.stats.noPhoneAlt}`}</Tag> : null}
                    </div>
                  </div>
                  <Button variant="primary" icon="repeat" disabled={!pending.stats.phones} onClick={() => setResendConfirmOpen(true)}>
                    {`שלח שוב צינתוק ל־${pending.stats.phones}`}
                  </Button>
                </div>
              ) : null}

              {resendStatus?.running ? (
                <div className="nx-stack" style={{ gap: 6 }}>
                  <small>{`שולח… ${resendStatus.sent + resendStatus.failed} / ${resendStatus.total}`}</small>
                  <ProgressBar value={resendStatus.total ? ((resendStatus.sent + resendStatus.failed) * 100) / resendStatus.total : 100} />
                </div>
              ) : null}
              {resendDone ? (
                <Banner tone={resendStatus.stoppedReason || resendStatus.failed ? 'warning' : 'success'} onClose={() => setResendStatus(null)}>
                  {`שליחה חוזרת הסתיימה: נשלחו ${resendStatus.sent}, נכשלו ${resendStatus.failed} מתוך ${resendStatus.total}`}
                  {resendStatus.campaigns ? ` (${resendStatus.campaigns} קמפיינים בימות)` : ''}
                  {resendStatus.stoppedReason ? ` — נעצרה: ${resendStatus.stoppedReason}` : ''}
                </Banner>
              ) : null}
            </div>
          </Panel>

          {/* ── אישור רישום ── */}
          <Panel
            title="אישור רישום"
            sub="שיחה יוצאת או צינתוק לנרשם אחרי אישור בקשת רישום"
            icon="check"
            tone="success"
            actions={<StatusToggle checked={!!regAutomation?.enabled} disabled={savingReg} onChange={onRegToggle} />}
          >
            <div className="nx-stack">
              <p className="nx-desc">אפשר טקסט (TTS), הקלטה או צינתוק. בקוד אישי — הוסיפו {'{personalCode}'} לטקסט רק אם רלוונטי.</p>
              <RadioList
                inline
                value={regMode}
                disabled={savingReg}
                onChange={(mode) => saveRegAutomation({ mode, enabled: false })}
                options={[{ value: 'tts', label: 'טקסט (TTS)' }, { value: 'recording', label: 'הקלטה' }, { value: 'tzintuk', label: 'צינתוק' }]}
                ariaLabel="סוג ההודעה"
              />
              {regMode === 'tzintuk' ? (
                <>
                  <p className="nx-desc">צלצול קצר לנייד, בלי הודעה. נשלח לנייד בלבד ועולה 0.1 יחידה.</p>
                  <Row>
                    <Button icon="phone" disabled={savingReg} onClick={() => openTest('reg')}>צינתוק בדיקה</Button>
                    <Counters automation={regAutomation} saving={savingReg} />
                  </Row>
                </>
              ) : regMode === 'tts' ? (
                <>
                  <Field label="טקסט להקראה" hint="מציינים: {systemName} · {personalCode} (אופציונלי)">
                    <TextArea
                      rows={2}
                      disabled={savingReg}
                      value={regAutomation?.ttsText ?? DEFAULT_REG_TTS}
                      onChange={(e) => setRegAutomation((prev) => ({ ...prev, ttsText: e.target.value }))}
                      onBlur={() => saveRegAutomation({ ttsText: regAutomation?.ttsText || DEFAULT_REG_TTS })}
                    />
                  </Field>
                  <Row>
                    <Button icon="phone" disabled={savingReg || !(regAutomation?.ttsText || '').trim()} onClick={() => openTest('reg')}>שיחת בדיקה</Button>
                    <Counters automation={regAutomation} saving={savingReg} />
                  </Row>
                </>
              ) : (
                <Row align="flex-end">
                  <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                    <Field label="תבנית להשמעה">
                      <Select
                        value={regAutomation?.templateId || ''}
                        disabled={savingReg}
                        placeholder="ללא"
                        onChange={(v) => saveRegAutomation({ templateId: v || null })}
                        options={templateOptions(regTemplates)}
                      />
                    </Field>
                  </div>
                  <Button icon="phone" disabled={!regAutomation?.templateId || savingReg} onClick={() => openTest('reg')}>שיחת בדיקה</Button>
                  <Counters automation={regAutomation} saving={savingReg} />
                </Row>
              )}
            </div>
          </Panel>

          {/* ── ספריית תבניות ── */}
          <Panel
            title="ספריית תבניות"
            sub={`${templates.length} תבניות`}
            icon="mic"
            tone="violet"
            actions={<Button size="sm" icon="plus" onClick={() => setCreateOpen(true)}>תבנית חדשה</Button>}
          >
            {templates.length ? (
              <div className="nx-list">
                {templates.map((tpl) => {
                  const busy = uploadingId === tpl.id;
                  return (
                    <div key={tpl.id} className="nx-item">
                      {tpl.hasAudio ? (
                        <IconButton icon={playingId === tpl.id ? 'pause' : 'play'} label={playingId === tpl.id ? 'עצירה' : 'השמעה'} onClick={() => playAudio(tpl)} />
                      ) : null}
                      <div className="nx-item__main">
                        <b>{displayTemplateName(tpl.name)}</b>
                        <small>{KEY_LABELS[tpl.key] || tpl.key}{tpl.audioFilename ? ` · ${tpl.audioFilename}` : ''}</small>
                        <div className="nx-item__tags">
                          {tpl.yemotSynced
                            ? <Tag tone="success" icon="check">מסונכרן לימות</Tag>
                            : <Tag tone="warning" icon="alert">חסרה הקלטה / סנכרון</Tag>}
                        </div>
                      </div>
                      <div className="nx-item__acts">
                        {busy ? <Spinner size={18} /> : null}
                        <Button size="sm" icon="upload" disabled={busy} onClick={() => fileInputRefs.current[tpl.id]?.click()}>העלאת קובץ</Button>
                        <Button size="sm" icon="mic" disabled={busy} onClick={() => setRecordTpl(tpl)}>הקלטה</Button>
                        <input
                          type="file"
                          accept="audio/*,.mp3,.wav,.ogg,.m4a,.webm"
                          hidden
                          ref={(el) => { fileInputRefs.current[tpl.id] = el; }}
                          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) uploadBlob(tpl.id, f, f.name); }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState compact icon="mic" title="אין תבניות עדיין" action={<Button variant="primary" icon="plus" onClick={() => setCreateOpen(true)}>תבנית חדשה</Button>}>
                צרו תבנית והעלו הקלטה.
              </EmptyState>
            )}
          </Panel>
        </>
      )}

      {/* יצירה */}
      <Dialog
        open={createOpen}
        onClose={() => !creating && setCreateOpen(false)}
        title="תבנית חדשה"
        icon="plus"
        size="sm"
        footer={(
          <>
            <Button variant="ghost" disabled={creating} onClick={() => setCreateOpen(false)}>ביטול</Button>
            <Button variant="primary" disabled={creating || !newName.trim()} onClick={handleCreate}>{creating ? 'יוצר…' : 'יצירה'}</Button>
          </>
        )}
      >
        <div className="nx-stack">
          <Field label="שם"><TextInput value={newName} onChange={(e) => setNewName(e.target.value)} data-autofocus /></Field>
          <Field label="סוג">
            <Select
              value={newKey}
              onChange={(key) => {
                setNewKey(key);
                if (key === 'registration_approved' && newName === DEFAULT_WIFE_NAME) setNewName(DEFAULT_REG_NAME);
                else if (key === 'wife_exam_encourage' && newName === DEFAULT_REG_NAME) setNewName(DEFAULT_WIFE_NAME);
              }}
              options={[
                { value: 'wife_exam_encourage', label: 'פידבק אחרי בחינה' },
                { value: 'registration_approved', label: 'אישור רישום' },
                { value: 'general', label: 'כללי' },
              ]}
            />
          </Field>
        </div>
      </Dialog>

      {/* הקלטה */}
      <Dialog
        open={!!recordTpl}
        onClose={() => uploadingId !== recordTpl?.id && setRecordTpl(null)}
        title={`הקלטת תבנית — ${displayTemplateName(recordTpl?.name)}`}
        icon="mic"
        size="sm"
        footer={<Button variant="ghost" disabled={uploadingId === recordTpl?.id} onClick={() => setRecordTpl(null)}>סגירה</Button>}
      >
        {recordTpl ? (
          <AudioRecorder
            key={recordTpl.id}
            onSave={(blob) => uploadBlob(recordTpl.id, blob, 'recording.webm')}
            saving={uploadingId === recordTpl.id}
            disabled={uploadingId === recordTpl.id}
          />
        ) : null}
      </Dialog>

      {/* שיחת בדיקה */}
      <Dialog
        open={testOpen}
        onClose={() => !testing && setTestOpen(false)}
        title={testKind === 'reg' ? 'שיחת בדיקה — אישור רישום' : 'שיחת בדיקה — פידבק בחינה'}
        icon="phone"
        size="sm"
        footer={(
          <>
            <Button variant="ghost" disabled={testing} onClick={() => setTestOpen(false)}>ביטול</Button>
            <Button variant="primary" icon="phone" disabled={testing || !testPhone.trim()} onClick={runTestCall}>{testing ? 'מחייג…' : 'חייג'}</Button>
          </>
        )}
      >
        <Field label="מספר טלפון">
          <TextInput ltr value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="05xxxxxxxx" data-autofocus onKeyDown={(e) => { if (e.key === 'Enter') runTestCall(); }} />
        </Field>
      </Dialog>

      <ConfirmDialog
        open={resendConfirmOpen}
        onClose={() => setResendConfirmOpen(false)}
        onConfirm={runResend}
        title="שליחה חוזרת"
        message={<p className="ck-confirm__msg">{`יחויגו ${pending?.stats.phones || 0} מספרים עם הודעת הפידבק. להמשיך?`}</p>}
        confirmLabel="שלח"
        danger={false}
      />
    </div>
  );
}
